import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { InsightEntity, InsightSchema } from '@/entities/Insights';
import { InsightsAccordionKeys } from '@/layouts/OrganizationLayout/NoteContent/insights/InsightsLists/types';
import {
  InsightsScopeTabKey,
  INSIGHTS_SCOPE_TABS,
} from '@/layouts/OrganizationLayout/NoteContent/insights/InsightsTabContent/types';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { TabsStore } from '@/stores/local/shared/TabsStore';
import call from '@/utils/api';
import { schemeParseMultiple } from '@/utils/schemeParse';

import { ConfirmPopupStore } from '../ConfirmPopupStore';

import { InsightsAccordionItemsStore } from './InsightsAccordionItemsStore';

export type InsightsStoreParams = {
  organizationId: string;
};

export class InsightsStore implements ILocalStore {
  private readonly _organizationId: string;
  private _lastRemovedId: string | null = null;
  readonly meta = new MetaStore();
  readonly popupMeta = new MetaStore();
  readonly activeItems = new InsightsAccordionItemsStore([InsightsAccordionKeys.active]);
  readonly scopeTabs = new TabsStore({ items: INSIGHTS_SCOPE_TABS });
  readonly active = new CollectionStore<InsightEntity, InsightEntity>({
    parseItem: (data: InsightEntity) => data,
    getEntityKey: (data: InsightEntity) => String(data.id),
  });
  readonly archive = new CollectionStore<InsightEntity, InsightEntity>({
    parseItem: (data: InsightEntity) => data,
    getEntityKey: (data: InsightEntity) => String(data.id),
  });
  readonly removePopup: ConfirmPopupStore;
  readonly archivePopup: ConfirmPopupStore;
  readonly activatePopup: ConfirmPopupStore;

  constructor({ organizationId }: InsightsStoreParams) {
    this._organizationId = organizationId;
    this.removePopup = new ConfirmPopupStore({
      onConfirm: (entityId) => this._onRemove(this._organizationId, entityId),
    });
    this.archivePopup = new ConfirmPopupStore({
      onConfirm: (entityId) => this._onArchive(this._organizationId, entityId),
    });
    this.activatePopup = new ConfirmPopupStore({
      onConfirm: (entityId) => this._onActivate(this._organizationId, entityId),
    });

    makeObservable<InsightsStore, '_lastRemovedId'>(this, {
      _lastRemovedId: observable,
      isEmpty: computed,
      lastRemovedId: computed,
      fetchInsights: action.bound,
      clearLastRemoved: action.bound,
    });
  }

  get isEmpty() {
    return this.active.isEmpty && this.archive.isEmpty;
  }

  get lastRemovedId(): string | null {
    return this._lastRemovedId;
  }

  clearLastRemoved() {
    this._lastRemovedId = null;
  }

  async remove(entityId: string) {
    return this._onRemove(this._organizationId, entityId);
  }

  private async _onActivate(organizationId: string, entityId: string) {
    this.popupMeta.setLoadedStartMeta();

    const response = await call<{ items: InsightEntity[] }>({
      endpoint: ENDPOINTS.insights.activate(organizationId, entityId),
      method: 'PUT',
    });

    runInAction(() => {
      if (response.isError) {
        notificationsStore.showError(response.error.message ?? 'Не удалось разархивировать инсайт');
        this.popupMeta.setLoadedErrorMeta();

        return;
      }

      notificationsStore.showSuccess('Инсайт разархивирован');
      this.popupMeta.setLoadedSuccessMeta();
      this.fetchInsights();
    });
  }

  private async _onArchive(organizationId: string, entityId: string) {
    this.popupMeta.setLoadedStartMeta();

    const response = await call<{ items: InsightEntity[] }>({
      endpoint: ENDPOINTS.insights.archive(organizationId, entityId),
      method: 'PUT',
    });

    runInAction(() => {
      if (response.isError) {
        notificationsStore.showError(response.error.message ?? 'Не удалось архивировать инсайт');
        this.popupMeta.setLoadedErrorMeta();

        return;
      }

      notificationsStore.showSuccess('Инсайт архивирован');
      this.popupMeta.setLoadedSuccessMeta();
      this.fetchInsights();
    });
  }

  private async _onRemove(organizationId: string, entityId: string) {
    this.popupMeta.setLoadedStartMeta();

    const response = await call<{ items: InsightEntity[] }>({
      endpoint: ENDPOINTS.insights.delete(organizationId, entityId),
      method: 'DELETE',
    });

    runInAction(() => {
      if (response.isError) {
        notificationsStore.showError(response.error.message ?? 'Не удалось удалить инсайт');
        this.popupMeta.setLoadedErrorMeta();

        return;
      }

      notificationsStore.showSuccess('Инсайт удален');
      this.popupMeta.setLoadedSuccessMeta();
      this._lastRemovedId = entityId;
      this.fetchInsights();
    });
  }

  async fetchInsights() {
    this.active.clear();
    this.archive.clear();

    this.meta.setLoadedStartMeta();

    const response = await call<{ items: InsightEntity[] }>({
      endpoint: ENDPOINTS.insights.list(this._organizationId),
      data: this.scopeTabs.activeKey === InsightsScopeTabKey.public ? { mode: 'public' } : { mode: 'my' },
    });

    runInAction(() => {
      if (response.isError) {
        this.meta.setLoadedErrorMeta();

        return;
      }

      const parsed = schemeParseMultiple(InsightSchema, response.data.items);

      const map = parsed.reduce<Record<InsightsAccordionKeys, InsightEntity[]>>(
        (acc, item) => {
          if (item.isArchived) {
            acc.archive.push(item);
          } else {
            acc.active.push(item);
          }

          return acc;
        },
        { active: [], archive: [] },
      );

      this.active.pushRaws(map.active);
      this.archive.pushRaws(map.archive);
      this.meta.setLoadedSuccessMeta();
    });
  }

  destroy() {
    this.meta.destroy();
    this.popupMeta.destroy();
    this.activeItems.destroy();
    this.active.destroy();
    this.archive.destroy();
    this.removePopup.destroy();
  }
}
